import axios from 'axios';
import { CYLINDO_ACCOUNT_ID } from 'commons/constants';
import { logError } from 'commons/logger';
import { probeLatestVersion } from '../../useCylindoDimensionShots/probeLatestVersion';
import { CYLINDO_CONTENT_API } from './defaults';

export const EMPTY_ENTRY = Object.freeze({
  config: null,
  version: null,
  loading: false,
});

const cylindoConfigPromises = {};
const cylindoStoreEntries = {};
const cylindoStoreListeners = new Set();

const fetchCylindoConfig = (productId, version) => {
  const suffix = version != null ? `?version=${version}` : '';
  const key = `${productId}${suffix}`;
  if (!cylindoConfigPromises[key]) {
    cylindoConfigPromises[key] = axios.get(
      `${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/configuration${suffix}`
    );
  }
  return cylindoConfigPromises[key];
};

const notifyCylindoStore = () => {
  cylindoStoreListeners.forEach(fn => fn());
};

export const subscribeToCylindoStore = listener => {
  cylindoStoreListeners.add(listener);
  return () => {
    cylindoStoreListeners.delete(listener);
  };
};

export const getCylindoEntry = productId =>
  productId ? cylindoStoreEntries[productId] || EMPTY_ENTRY : EMPTY_ENTRY;

export const requestCylindoFetch = productId => {
  if (!productId || cylindoStoreEntries[productId]) return;

  cylindoStoreEntries[productId] = Object.freeze({
    config: null,
    version: null,
    loading: true,
  });
  notifyCylindoStore();

  fetchCylindoConfig(productId)
    .then(res => {
      if (!res.data) {
        cylindoStoreEntries[productId] = EMPTY_ENTRY;
        notifyCylindoStore();
        return null;
      }
      // The unversioned response above can be a stale CDN/browser copy (Cylindo caches it
      // for a year, CON-8451), so re-read the config at the probed version - versioned
      // responses are max-age=0 and always reflect the current option codes.
      return probeLatestVersion(productId, res.data.version)
        .then(version =>
          fetchCylindoConfig(productId, version)
            .then(fresh => ({ config: fresh.data || res.data, version }))
            .catch(() => ({ config: res.data, version }))
        )
        .then(({ config, version }) => {
          cylindoStoreEntries[productId] = Object.freeze({
            config,
            version,
            loading: false,
          });
          notifyCylindoStore();
        });
    })
    .catch(e => {
      logError(e);
      cylindoStoreEntries[productId] = EMPTY_ENTRY;
      notifyCylindoStore();
    });
};
